/**
 * useSpaceMessages — the member-to-member DISCUSSION thread for a Connect Space.
 *
 * This is the human-to-human conversation between the two collaborating
 * companies — DISTINCT from the scoped AI Q&A (``askInSpace`` / ``ConnectChat``,
 * which is one-shot LLM turns with no shared server-side thread). Both endpoints
 * sit behind the SAME ``require_space_access`` gate as ``askInSpace``, so the
 * hooks are GUEST-CREDENTIAL-AWARE exactly like ``getSpaceScope``:
 *   - authed Ando member → omit ``guestCredential`` (cookie path).
 *   - magic-link guest   → pass the stored ConnectGuest credential.
 *
 * Near-real-time via a 7s ``refetchInterval`` (the thread is low-volume and
 * human-paced; no websocket needed for this surface). Posting is OPTIMISTIC:
 * the draft is appended immediately under a temporary id, then the cache is
 * invalidated so the authoritative server row (with its real id + resolved
 * ``author_label`` + ``is_own``) replaces it on the next fetch.
 *
 * BE surface: ``ando-agent/src/api/v1/endpoints/connect.py``
 * (``GET/POST /connect/spaces/{id}/messages``).
 */

import { type ConnectMessage, listSpaceMessages, postSpaceMessage } from "@ando/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { connectQueryKey } from "./useConnectSpaces.js";

/** Near-real-time poll cadence for the human discussion thread. */
const MESSAGES_REFETCH_MS = 7_000;

/**
 * A short, stable discriminator for the access credential so an authed-member
 * thread and a magic-link guest thread for the SAME ``spaceId`` never share a
 * cache entry (cross-credential bleed). The raw credential is NOT folded into
 * the key (avoids leaking the token into devtools / cache snapshots): we use
 * ``"member"`` for the cookie path and a stable per-token bucket for guests.
 */
function credentialDiscriminator(guestCredential?: string | null): string {
  if (!guestCredential) return "member";
  let hash = 0;
  for (let i = 0; i < guestCredential.length; i += 1) {
    hash = (hash * 31 + guestCredential.charCodeAt(i)) | 0;
  }
  return `guest-${(hash >>> 0).toString(36)}`;
}

export function spaceMessagesQueryKey(
  spaceId: string,
  guestCredential?: string | null,
): readonly unknown[] {
  return [
    ...connectQueryKey.detail(spaceId),
    "messages",
    credentialDiscriminator(guestCredential),
  ] as const;
}

export interface UseSpaceMessagesResult {
  readonly messages: readonly ConnectMessage[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

export function useSpaceMessages(
  spaceId: string,
  guestCredential?: string | null,
  options: { enabled?: boolean } = {},
): UseSpaceMessagesResult {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: spaceMessagesQueryKey(spaceId, guestCredential),
    queryFn: () => listSpaceMessages(spaceId, undefined, guestCredential),
    enabled,
    staleTime: MESSAGES_REFETCH_MS,
    refetchInterval: MESSAGES_REFETCH_MS,
  });

  return {
    messages: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

let OPTIMISTIC_SEQ = 0;
function nextOptimisticId(): string {
  OPTIMISTIC_SEQ += 1;
  return `connect-msg-optimistic-${OPTIMISTIC_SEQ}`;
}

/**
 * Post a message to the discussion thread (optimistic append + invalidate).
 *
 * The optimistic row is right-aligned (``is_own: true``) so the sender sees
 * their message land instantly; the cache invalidation then reconciles it with
 * the authoritative server row (real id, resolved author label). On error the
 * optimistic row is rolled back from the cache.
 */
export function usePostSpaceMessage(spaceId: string, guestCredential?: string | null) {
  const qc = useQueryClient();
  const key = spaceMessagesQueryKey(spaceId, guestCredential);
  return useMutation<ConnectMessage, Error, string, { previous?: readonly ConnectMessage[] }>({
    mutationFn: (body) => postSpaceMessage(spaceId, body, guestCredential),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<readonly ConnectMessage[]>(key);
      const optimistic: ConnectMessage = {
        id: nextOptimisticId(),
        space_id: spaceId,
        author_label: "",
        author_member_id: null,
        body,
        created_at: new Date().toISOString(),
        is_own: true,
      };
      qc.setQueryData<readonly ConnectMessage[]>(key, [...(previous ?? []), optimistic]);
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}
